import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import {
    Box,
    Collapse,
    Divider,
    IconButton,
    Stack,
    Typography,
} from "@mui/material";
import React, { useState } from "react";
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer-continued";

import AcxButton from "components/UI/AcxButton";
import { AcxMessageBox } from "components/UI/AcxMessageBox";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxToggleButton from "components/UI/AcxToggleButton";
import * as AYSDialog from "components/UI/AYSDialog";
import type { LLMClassifierSaveChanges } from "../utils/llmClassifierSaveChanges";

const DIFF_VIEW_STORAGE_KEY = "llm-classifier-save-diff-view";
const DIFF_WRAP_STORAGE_KEY = "llm-classifier-save-diff-wrap";
const MaxDescriptionLength = 250;

type DiffView = "unified" | "split";
type WrapMode = "wrap" | "nowrap";

interface SaveLLMClassifierConfirmationDialogProps {
    isOpen: boolean;
    isLoading: boolean;
    willTemplateChange: boolean;
    willPublishedGroupChange: boolean;
    versionDescription: string;
    changes?: LLMClassifierSaveChanges;
    onVersionDescriptionChange: (value: string) => void;
    onClose: () => void;
    onConfirm: () => void;
}

const readDiffViewPreference = (): DiffView =>
    localStorage.getItem(DIFF_VIEW_STORAGE_KEY) === "split"
        ? "split"
        : "unified";

const readWrapPreference = (): WrapMode =>
    localStorage.getItem(DIFF_WRAP_STORAGE_KEY) === "nowrap"
        ? "nowrap"
        : "wrap";

export function SaveLLMClassifierConfirmationDialog({
    isOpen,
    isLoading,
    willTemplateChange,
    willPublishedGroupChange,
    versionDescription,
    changes,
    onVersionDescriptionChange,
    onClose,
    onConfirm,
}: SaveLLMClassifierConfirmationDialogProps) {
    const [showRawDiff, setShowRawDiff] = useState(false);
    const [diffView, setDiffView] = useState<DiffView>(readDiffViewPreference);
    const [wrapMode, setWrapMode] = useState<WrapMode>(readWrapPreference);
    const hasChanges = changes?.hasChanges === true;
    const canSave = versionDescription.trim().length > 0 && hasChanges;

    const handleViewChange = (value: DiffView | null) => {
        if (!value) return;
        setDiffView(value);
        localStorage.setItem(DIFF_VIEW_STORAGE_KEY, value);
    };

    const handleWrapChange = (value: WrapMode | null) => {
        if (!value) return;
        setWrapMode(value);
        localStorage.setItem(DIFF_WRAP_STORAGE_KEY, value);
    };

    return (
        <AYSDialog.Container
            isOpen={isOpen}
            onClose={onClose}
            icon={<InfoRoundedIcon color="primary" sx={{ fontSize: 32 }} />}
            maxWidth="lg"
        >
            <AYSDialog.Title>Confirm model changes</AYSDialog.Title>
            <AYSDialog.Subtitle>
                Review what changed before creating a new model version.
            </AYSDialog.Subtitle>
            <AYSDialog.Content
                sx={{ padding: "0 !important", background: "none" }}
            >
                <Stack gap={2}>
                    <Stack gap={1}>
                        {willTemplateChange && (
                            <AcxMessageBox variant="warning">
                                You changed fields that will update the
                                template.
                            </AcxMessageBox>
                        )}
                        {willPublishedGroupChange && (
                            <AcxMessageBox variant="warning">
                                The model group changed. Published Signals
                                charts may move after save.
                            </AcxMessageBox>
                        )}
                    </Stack>

                    <Stack gap={1}>
                        <Typography
                            color="gray.700"
                            fontSize="16px"
                            fontWeight={600}
                            lineHeight="24px"
                        >
                            Version description
                        </Typography>
                        <Typography
                            color="gray.500"
                            fontSize="13px"
                            lineHeight="20px"
                        >
                            Document what changed in this model version.
                        </Typography>
                        <AcxMainTextField
                            id="model-version-description"
                            labelText="Version Description"
                            fullWidth
                            rows={4}
                            multiline
                            maxLength={MaxDescriptionLength}
                            multilinePadding
                            labelEndText={`Max ${MaxDescriptionLength} characters`}
                            value={versionDescription}
                            onChange={(e) =>
                                onVersionDescriptionChange(e.target.value)
                            }
                        />
                    </Stack>

                    <Divider />

                    <Stack gap={1}>
                        <Typography
                            color="gray.700"
                            fontSize="16px"
                            fontWeight={600}
                            lineHeight="24px"
                        >
                            Changes
                        </Typography>
                        {!hasChanges && (
                            <AcxMessageBox variant="info">
                                No model changes were detected.
                            </AcxMessageBox>
                        )}
                        {changes?.sections.map((section) => (
                            <Box
                                key={section.title}
                                sx={{
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 1,
                                    backgroundColor: "white.main",
                                    padding: 1.5,
                                }}
                            >
                                <Typography
                                    color="gray.700"
                                    fontSize="14px"
                                    fontWeight={600}
                                    lineHeight="20px"
                                    mb={1}
                                >
                                    {section.title}
                                </Typography>
                                <Stack component="ul" gap={0.75} pl={2.5} m={0}>
                                    {section.changes.map((change) => (
                                        <Typography
                                            component="li"
                                            key={change}
                                            color="gray.600"
                                            fontSize="13px"
                                            lineHeight="20px"
                                        >
                                            {change}
                                        </Typography>
                                    ))}
                                </Stack>
                            </Box>
                        ))}
                    </Stack>

                    {changes && (
                        <Stack gap={1}>
                            <Box
                                sx={{
                                    alignItems: "center",
                                    display: "flex",
                                    justifyContent: "space-between",
                                }}
                            >
                                <Typography
                                    color="gray.700"
                                    fontSize="14px"
                                    fontWeight={600}
                                    lineHeight="20px"
                                >
                                    Raw diff
                                </Typography>
                                <IconButton
                                    aria-label="toggle raw diff"
                                    onClick={() =>
                                        setShowRawDiff((current) => !current)
                                    }
                                    size="small"
                                    sx={{
                                        transform: showRawDiff
                                            ? "rotate(180deg)"
                                            : "rotate(0deg)",
                                        transition: "transform 150ms ease",
                                    }}
                                >
                                    <ExpandMoreRoundedIcon fontSize="small" />
                                </IconButton>
                            </Box>
                            <Collapse in={showRawDiff}>
                                <Stack gap={1}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            gap: 1,
                                        }}
                                    >
                                        <AcxToggleButton
                                            id="llm-classifier-diff-wrap-toggle"
                                            exclusive
                                            defaultValue={wrapMode}
                                            onChange={handleWrapChange}
                                            items={[
                                                {
                                                    value: "wrap",
                                                    element: "Wrap",
                                                },
                                                {
                                                    value: "nowrap",
                                                    element: "No wrap",
                                                },
                                            ]}
                                        />
                                        <AcxToggleButton
                                            id="llm-classifier-diff-view-toggle"
                                            exclusive
                                            defaultValue={diffView}
                                            onChange={handleViewChange}
                                            items={[
                                                {
                                                    value: "unified",
                                                    element: "Unified",
                                                },
                                                {
                                                    value: "split",
                                                    element: "Split",
                                                },
                                            ]}
                                        />
                                    </Box>
                                    <Box
                                        sx={{
                                            maxHeight: 360,
                                            overflowY: "auto",
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 1,
                                            fontSize: 12,
                                        }}
                                    >
                                        <ReactDiffViewer
                                            oldValue={changes.oldJson}
                                            newValue={changes.newJson}
                                            splitView={diffView === "split"}
                                            compareMethod={DiffMethod.WORDS}
                                            useDarkTheme={false}
                                            hideLineNumbers={false}
                                            showDiffOnly
                                            styles={{
                                                diffContainer: {
                                                    minWidth: "unset",
                                                    overflowX:
                                                        wrapMode === "nowrap"
                                                            ? "auto"
                                                            : "unset",
                                                },
                                                line: {
                                                    fontFamily: "monospace",
                                                    fontSize: 12,
                                                },
                                                content: { fontSize: 12 },
                                                contentText:
                                                    wrapMode === "nowrap"
                                                        ? {
                                                              whiteSpace: "pre",
                                                              lineBreak: "auto",
                                                          }
                                                        : undefined,
                                            }}
                                        />
                                    </Box>
                                </Stack>
                            </Collapse>
                        </Stack>
                    )}
                </Stack>
            </AYSDialog.Content>
            <AYSDialog.Actions>
                <AcxButton color="white" onClick={onClose} disabled={isLoading}>
                    Cancel
                </AcxButton>
                <AcxButton
                    color="primary"
                    onClick={onConfirm}
                    loading={isLoading}
                    disabled={!canSave}
                    disabledTooltip={
                        hasChanges
                            ? "Please enter a version description to save"
                            : "No model changes were detected"
                    }
                >
                    Save
                </AcxButton>
            </AYSDialog.Actions>
        </AYSDialog.Container>
    );
}
